/**
 * Shared admin helpers — workspace resolution + small UI primitives reused
 * across the admin sub-pages. Keeps each page file focused on its own data.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";

/** Resolve the current admin's primary workspace. Returns {workspace, loading}. */
export function useAdminWorkspace() {
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    api
      .get("/workspaces")
      .then((r) => {
        if (!alive) return;
        const list = r.data || [];
        const mine =
          list.find((w) => (w.admin_ids || []).includes(user.id)) || null;
        setWorkspace(mine);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [user]);
  return { workspace, loading };
}

export function AdminHeader({ eyebrow, title, action }) {
  return (
    <header
      data-testid="admin-page-header"
      className="flex items-end justify-between flex-wrap gap-3"
    >
      <div>
        <span className="eyebrow text-[#525252]">{eyebrow}</span>
        <h1 className="mt-2 font-serif text-3xl sm:text-4xl tracking-tight text-[#171717]">
          {title}
        </h1>
      </div>
      {action}
    </header>
  );
}

export function AdminEmpty({ children, testid }) {
  return (
    <p
      data-testid={testid}
      className="font-serif italic text-[#525252] rounded-2xl border hairline bg-white p-7"
    >
      {children}
    </p>
  );
}

/**
 * AdminEmptyCard — richer empty state for "real" first-time / no-data
 * surfaces (vs the lightweight AdminEmpty for loading + filter-no-match).
 *
 * Always renders an eyebrow + title + body + optional CTA, all on a clean
 * cream-tinted card. Mirrors the parent first-child empty state on /home so
 * the calm voice carries across both audiences.
 */
export function AdminEmptyCard({
  testid,
  eyebrow = "Nothing here yet",
  title,
  body,
  action,
}) {
  return (
    <section
      data-testid={testid}
      className="mt-8 rounded-3xl bg-white border hairline p-8 sm:p-10 max-w-2xl"
    >
      <span className="eyebrow text-[#525252]">{eyebrow}</span>
      <h3 className="mt-3 font-serif text-2xl sm:text-3xl text-[#171717] leading-tight">
        {title}
      </h3>
      {body && (
        <p className="mt-4 text-[15px] leading-relaxed text-[#525252]">{body}</p>
      )}
      {action && <div className="mt-7">{action}</div>}
    </section>
  );
}
